import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/utils";

/** Horizontal padding for each header layout. */
const INSET_CLASS = {
  none: "",
  /** Full-width page headers (PageHeader, Settings, Usage). */
  page: "px-4 md:px-8",
  /** Pages laid out in a `px-6` content column (repositories, Monitors). */
  content: "px-6",
  /** Codesmith session/workspace panes. */
  pane: "px-4",
  /** Codesmith new-session canvas. */
  canvas: "px-3 md:px-6",
  /** Contextual sidebars: title flush with the nav, icon buttons flush right. */
  sidebar: "pl-4 pr-2",
} as const;

const HEIGHT_CLASS = {
  /** Fixed header height; content must fit (sidebars, panes). */
  fixed: "h-app-header",
  /** Minimum header height, growing when content wraps (page headers). */
  min: "min-h-app-header",
  /** Minimum header height from md up; on narrow viewports, `py-4`
   * accommodates stacked breadcrumbs and controls. */
  "min-md": "py-4 max-md:min-h-0 md:min-h-app-header md:py-0",
} as const;

interface AppHeaderBarProps extends ComponentPropsWithoutRef<"div"> {
  as?: "div" | "header";
  inset?: keyof typeof INSET_CLASS;
  height?: keyof typeof HEIGHT_CLASS;
}

/** Shares `--spacing-app-header` across page, sidebar and pane headers. */
export function AppHeaderBar({
  as: Tag = "div",
  inset = "none",
  height = "fixed",
  className,
  ...props
}: AppHeaderBarProps) {
  return (
    <Tag
      className={cn(
        "flex shrink-0 items-center",
        HEIGHT_CLASS[height],
        INSET_CLASS[inset],
        className,
      )}
      {...props}
    />
  );
}
